import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSession } from '@/auth/Session';
import { useServiceToken } from '@/auth/index';
import { useAppConstantsContext } from '@/context/AppConstants';
import { serializedTypes, useTuxedo } from '@/reports/index';
import { LoadingDots } from '@/search/index';
import { Button, Divider } from '@/theme/components';
import { unformatPhone } from '@/utils/formatPhone';
import { schemaBubbleForm } from '@/utils/schemaForms/schemaForms';
import { FormStepOne } from './FormStepOne';
import { FormStepTwo } from './FormStepTwo';
import { Recommendation } from './Recommendation';
import { BubbleForm } from './types';
import { disclaimers } from './utils';
import { InitialRecordSearch } from '../../PerksPage';
import { Reward } from '../../rewards/types';
import { LogoTagContainer } from '../components';
import { logoClassStyle } from '../utils';

type ValidFormStep = 'stepOne' | 'stepTwo' | 'loading' | 'recommendation';

export const Bubble: React.FC<{
  reward: Reward;
  report?: serializedTypes.SerializedProperty;
  initialRecordSearch: InitialRecordSearch;
}> = ({ reward, report, initialRecordSearch }) => {
  const [formStep, setFormStep] = useState<ValidFormStep>('stepOne');

  const {
    session: { account },
  } = useSession();

  useServiceToken('tuxedo');

  const {
    data: { brandName },
  } = useAppConstantsContext();

  const { setupUserProfile, createUserProfile, recommendations } = useTuxedo();

  const useRecommentationQuery = recommendations(
    initialRecordSearch,
    reward.name
  );

  useEffect(() => {
    if (useRecommentationQuery.isSuccess) {
      setFormStep('recommendation');
    }
  }, [useRecommentationQuery]);

  const address = report?.parcel_address;
  const year_built = report?.buildings?.[0]?.year_built;
  const sqft = report?.buildings?.[0]?.square_footage?.total;
  const street_address =
    address?.parts?.street || initialRecordSearch.address || '';
  const state = address?.parsed?.state || initialRecordSearch.state || '';
  const city = address?.parsed?.city || initialRecordSearch.city || '';
  const zip = address?.parsed?.zip5 || initialRecordSearch.zip_code || '';

  const {
    register,
    formState: { errors },
    setValue,
    trigger,
    handleSubmit,
  } = useForm<BubbleForm>({
    mode: 'onSubmit',
    resolver: yupResolver(schemaBubbleForm),
    defaultValues: {
      not_full_time_residence: false,
      pre_existing_damage: false,
      not_important: false,
      unit_occupied: false,
      dwelling_vacant: false,
      none_apply: false,
      street_address,
      city,
      state,
      year_built: year_built?.toString() || '',
      square_footage: sqft?.toString() || '',
    },
  });

  const onSubmit = async (data: BubbleForm) => {
    const userInfo = account?.account?.user_info;

    const userProfile = setupUserProfile(userInfo, address);
    const userInterest = {
      category: reward.userInterestCategory,
      custom_attributes: {
        city: data.city,
        country: 'US',
        email: userProfile.email,
        first_name: data.first_name,
        full_address: `${data.street_address}, ${data.city}, ${data.state}, ${zip}, US`,
        last_name: data.last_name,
        phone: unformatPhone(data.phone),
        state: data.state,
        street_address: data.street_address,
        type: ['home'],
        zipcode: zip,
      },
      offers_opt_in: true,
    };

    if (!address) {
      userProfile.record_search = initialRecordSearch;
    }

    createUserProfile.mutate(
      {
        userProfile: {
          user_profile: userProfile,
          user_interests: [userInterest],
          record_search: userProfile.record_search,
        },
      },
      {
        onSuccess: () => {
          setFormStep('loading');
          useRecommentationQuery.refetch();
        },
      }
    );
  };

  const displayContent = () => {
    if (formStep === 'stepOne') {
      return (
        <FormStepOne
          register={register}
          setValue={setValue}
          rewardType={reward.rewardType}
          trigger={trigger}
          reportState={state}
        />
      );
    }
    if (formStep === 'stepTwo') {
      return (
        <FormStepTwo
          register={register}
          trigger={trigger}
          setValue={setValue}
        />
      );
    }
    if (formStep === 'recommendation') {
      return (
        <Recommendation
          reward={reward}
          initialRecordSearch={initialRecordSearch}
        />
      );
    }
  };

  const header = () => (
    <>
      <LogoTagContainer>
        <div style={logoClassStyle(reward.logo)} />
      </LogoTagContainer>
      <Divider sx={{ marginTop: [2.5], marginBottom: [2, 4] }} />
    </>
  );

  const navigation = () => {
    if (formStep === 'stepOne') {
      return (
        <>
          <Divider sx={{ marginBottom: [2, 4], marginTop: [4] }} />
          <Button
            variant="contained"
            color="success"
            sx={{ maxWidth: '172px', mb: [2, 4] }}
            fullWidth
            disabled={Object.keys(errors).length !== 0}
            onClick={() => setFormStep('stepTwo')}
          >
            Next
          </Button>
        </>
      );
    }
    if (formStep === 'stepTwo') {
      return (
        <>
          <Divider sx={{ marginY: [2] }} />
          <Button
            color="success"
            variant="contained"
            sx={{ maxWidth: '172px' }}
            fullWidth
            disabled={Object.keys(errors).length !== 0}
            onClick={handleSubmit(onSubmit)}
          >
            Submit
          </Button>
        </>
      );
    }
  };

  const footer = () => {
    if (['stepTwo', 'recommendation'].includes(formStep)) {
      return (
        <>
          <Divider sx={{ mb: [2], mt: [2, 4] }} />
          {disclaimers(brandName)}
        </>
      );
    }
  };

  if (createUserProfile.isLoading || useRecommentationQuery.isFetching) {
    return (
      <>
        {header()}
        <LoadingDots count={5} />
      </>
    );
  }

  return (
    <>
      {header()}
      {displayContent()}
      {navigation()}
      {footer()}
    </>
  );
};
